export type TerritoryRegion =
  | "EU"
  | "UK"
  | "NA"
  | "LATAM"
  | "SA"
  | "AFRICA"
  | "MEENA"
  | "APAC";

export interface Territory {
  code: string;
  name: string;
}

const displayNames =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const FALLBACK_TERRITORY_NAMES: Record<string, string> = {
  XK: "Kosovo",
  CW: "Curaçao",
  SX: "Sint Maarten",
  BQ: "Bonaire, Sint Eustatius and Saba",
  BL: "Saint Barthélemy",
  MF: "Saint Martin",
  GP: "Guadeloupe",
  MQ: "Martinique",
  YT: "Mayotte",
  RE: "Réunion",
  TF: "French Southern Territories",
  UM: "U.S. Outlying Islands",
  PS: "Palestine",
};

const resolveTerritoryName = (code: string): string => {
  const normalized = code.toUpperCase();
  try {
    const resolved = displayNames?.of(normalized);
    if (resolved) return resolved;
  } catch {
    // Intl.DisplayNames throws RangeError for invalid region codes (e.g. "WORLDWIDE")
  }
  return FALLBACK_TERRITORY_NAMES[normalized] || normalized;
};

const REGION_CODES: Record<TerritoryRegion, string[]> = {
  EU: [
    "AD",
    "AL",
    "AM",
    "AT",
    "AX",
    "AZ",
    "BA",
    "BE",
    "BG",
    "BY",
    "CH",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FO",
    "FR",
    "GE",
    "GI",
    "GR",
    "HR",
    "HU",
    "IE",
    "IS",
    "IT",
    "LI",
    "LT",
    "LU",
    "LV",
    "MC",
    "MD",
    "ME",
    "MK",
    "MT",
    "NL",
    "NO",
    "PL",
    "PT",
    "RO",
    "RS",
    "RU",
    "SE",
    "SI",
    "SJ",
    "SK",
    "SM",
    "UA",
    "VA",
    "XK",
  ],
  UK: ["GB", "GG", "JE", "IM"],
  NA: ["CA", "US", "GL", "BM", "PM"],
  LATAM: [
    "AG",
    "AI",
    "AW",
    "BB",
    "BL",
    "BQ",
    "BS",
    "BZ",
    "CR",
    "CU",
    "CW",
    "DM",
    "DO",
    "GD",
    "GP",
    "GT",
    "HN",
    "HT",
    "JM",
    "KN",
    "KY",
    "LC",
    "MF",
    "MQ",
    "MS",
    "MX",
    "NI",
    "PA",
    "PR",
    "SV",
    "SX",
    "TC",
    "TT",
    "VC",
    "VG",
    "VI",
  ],
  SA: [
    "AR",
    "BO",
    "BR",
    "CL",
    "CO",
    "EC",
    "FK",
    "GF",
    "GS",
    "GY",
    "PE",
    "PY",
    "SR",
    "UY",
    "VE",
  ],
  AFRICA: [
    "AO",
    "BF",
    "BI",
    "BJ",
    "BW",
    "CD",
    "CF",
    "CG",
    "CI",
    "CM",
    "CV",
    "DJ",
    "ER",
    "ET",
    "GA",
    "GH",
    "GM",
    "GN",
    "GQ",
    "GW",
    "KE",
    "KM",
    "LR",
    "LS",
    "MG",
    "MU",
    "ML",
    "MW",
    "MZ",
    "NA",
    "NE",
    "NG",
    "RE",
    "RW",
    "SC",
    "SH",
    "SL",
    "SN",
    "SO",
    "SS",
    "ST",
    "SZ",
    "TD",
    "TF",
    "TG",
    "TZ",
    "UG",
    "YT",
    "ZA",
    "ZM",
    "ZW",
  ],
  MEENA: [
    "AE",
    "BH",
    "DZ",
    "EG",
    "EH",
    "IL",
    "IQ",
    "IR",
    "JO",
    "KW",
    "LB",
    "LY",
    "MA",
    "MR",
    "OM",
    "PS",
    "QA",
    "SA",
    "SD",
    "SY",
    "TN",
    "TR",
    "YE",
  ],
  APAC: [
    "AF",
    "AS",
    "AU",
    "BD",
    "BN",
    "BT",
    "CC",
    "CK",
    "CN",
    "CX",
    "FJ",
    "FM",
    "GU",
    "HK",
    "HM",
    "ID",
    "IN",
    "IO",
    "JP",
    "KG",
    "KH",
    "KI",
    "KP",
    "KR",
    "KZ",
    "LA",
    "LK",
    "NP",
    "MM",
    "MN",
    "MO",
    "MP",
    "MH",
    "MV",
    "MY",
    "NC",
    "NF",
    "NR",
    "NU",
    "NZ",
    "PF",
    "PG",
    "PH",
    "PK",
    "PN",
    "PW",
    "SB",
    "SG",
    "TH",
    "TJ",
    "TK",
    "TL",
    "TM",
    "TO",
    "TV",
    "TW",
    "UM",
    "UZ",
    "VN",
    "VU",
    "WF",
    "WS",
    "AQ",
    "BV",
  ],
};

const toTerritory = (code: string): Territory => ({
  code,
  name: resolveTerritoryName(code),
});

export const TERRITORY_ZONES: Record<TerritoryRegion, Territory[]> =
  Object.fromEntries(
    Object.entries(REGION_CODES).map(([region, codes]) => [
      region,
      codes
        .map((code) => toTerritory(code))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ])
  ) as Record<TerritoryRegion, Territory[]>;

export const REGION_LABELS: Record<TerritoryRegion, string> = {
  EU: "Europe",
  UK: "United Kingdom",
  // Not "North America": that name is the PRESET (US + CA + MX, data/territory-presets.ts, DEC-070),
  // and two things in one picker are never called the same (spec §3.4). This region is CA, US and
  // the three North Atlantic territories (GL, BM, PM) — no Mexico.
  NA: "US, Canada & territories",
  LATAM: "Latin America & Caribbean",
  SA: "South America",
  AFRICA: "Africa",
  MEENA: "Middle East & North Africa",
  APAC: "Asia-Pacific",
};

export const ALL_TERRITORIES: Territory[] = Array.from(
  new Map(
    Object.values(TERRITORY_ZONES)
      .flat()
      .map((territory) => [territory.code.toUpperCase(), territory])
  ).values()
).sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT_REGION: TerritoryRegion = "APAC";

const codeToRegion = new Map<string, TerritoryRegion>();
const codeToName = new Map<string, string>();
for (const [region, countries] of Object.entries(TERRITORY_ZONES) as Array<
  [TerritoryRegion, Territory[]]
>) {
  countries.forEach((country) => {
    codeToRegion.set(country.code.toUpperCase(), region);
    codeToName.set(country.code.toUpperCase(), country.name);
  });
}

export const getTerritoryInfo = (
  code: string
): { region: TerritoryRegion; name: string; code: string } => {
  const normalized = code.toUpperCase();
  const region = codeToRegion.get(normalized) || DEFAULT_REGION;
  const name = codeToName.get(normalized) || resolveTerritoryName(normalized);
  return { region, name, code: normalized };
};

export const groupTerritoriesByRegion = (codes: string[]) => {
  const groups: Record<TerritoryRegion, Territory[]> = {
    EU: [],
    UK: [],
    NA: [],
    LATAM: [],
    SA: [],
    AFRICA: [],
    MEENA: [],
    APAC: [],
  };

  codes.forEach((code) => {
    const trimmed = code.trim();
    // Skip non-ISO codes like "Worldwide" — these aren't real country codes
    if (!trimmed || trimmed.length > 3) return;
    const info = getTerritoryInfo(trimmed);
    if (!groups[info.region].some((c) => c.code === info.code)) {
      groups[info.region].push({ code: info.code, name: info.name });
    }
  });

  return groups;
};
